<template>
  <div class="unified-platform-hub-page container-fluid py-4">
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── VIEW 1: MASTER PLATFORMS GRID (!selectedPlatform) ─────────────── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div v-if="!selectedPlatform">
      <!-- Page Header -->
      <div class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3">
            <div class="ms-page-header__icon">
              <i class="fa-solid fa-shapes"></i>
            </div>
            <div>
              <p class="ms-page-header__breadcrumb">
                Platform Delivery <span class="ms-breadcrumb-sep">/</span> Unified Platform Hub
              </p>
              <h1 class="ms-page-header__title">Unified Platform Hub</h1>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <router-link
              to="/platform-settings/user-settings"
              class="btn btn-header-secondary fw-bold px-3 py-2 text-sm d-flex align-items-center gap-2 text-decoration-none shadow-xs"
              title="Configure your personal Atlassian API token in User Settings"
            >
              <i class="fa-solid fa-key text-info"></i>
              <span>My Atlassian Token</span>
            </router-link>
            <button 
              type="button"
              class="btn btn-cyan-primary fw-bold px-3 py-2 text-sm d-flex align-items-center gap-2 shadow-xs"
              @click="openAddPlatformModal"
              :disabled="isSubmitting"
            >
              <i class="fa-solid fa-plus"></i>
              <span>Register New Platform</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Search, Filter Toolbar -->
      <div class="card border-0 shadow-sm bg-white mb-4 filter-toolbar-card">
        <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <!-- Search Input -->
          <div class="d-flex align-items-center gap-2 flex-grow-1" style="max-width: 460px;">
            <div class="position-relative flex-grow-1">
              <i class="fa-solid fa-magnifying-glass text-muted-custom search-icon"></i>
              <input 
                type="text" 
                class="form-control form-control-sm ps-4 pe-4 bg-light border-0 focus-glow text-xs" 
                v-model="searchQuery" 
                placeholder="Search platforms by name, key, or description..." 
              />
              <button 
                v-if="searchQuery" 
                @click="searchQuery = ''" 
                class="btn btn-link btn-xs text-muted-custom position-absolute end-0 top-50 translate-middle-y me-1 p-0 text-decoration-none"
              >
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          <!-- Card Default Tab Filter -->
          <div class="d-flex align-items-center gap-2 p-1 bg-light rounded-3 border">
            <span class="text-xxs fw-bold text-muted-custom px-2 text-uppercase">Card View:</span>
            <button
              type="button"
              class="btn btn-xs rounded-2 px-3 py-1 fw-bold"
              :class="globalCardTab === 'regression' ? 'bg-white text-dark-blue shadow-xs' : 'text-secondary'"
              @click="globalCardTab = 'regression'"
            >
              <i class="fa-solid fa-list-check me-1 text-info"></i>Regression
            </button>
            <button
              type="button"
              class="btn btn-xs rounded-2 px-3 py-1 fw-bold"
              :class="globalCardTab === 'bug_report' ? 'bg-white text-dark-blue shadow-xs' : 'text-secondary'"
              @click="globalCardTab = 'bug_report'"
            >
              <i class="fa-solid fa-bug me-1 text-danger"></i>Bugs
            </button>
            <button
              type="button"
              class="btn btn-xs rounded-2 px-3 py-1 fw-bold"
              :class="globalCardTab === 'releases' ? 'bg-white text-dark-blue shadow-xs' : 'text-secondary'"
              @click="globalCardTab = 'releases'"
            >
              <i class="fa-solid fa-code-branch me-1 text-cyan-primary"></i>Releases
            </button>
          </div>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-info" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Loading platform delivery hub...</span>
        </div>
        <p class="text-muted-custom mt-3 fw-bold">Loading Unified Platform Delivery Hub...</p>
      </div>

      <!-- Platforms Grid -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="filteredPlatforms.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
          <i class="fa-solid fa-shapes fa-3x mb-3 text-light"></i>
          <h5 class="fw-bold text-dark-blue">No Platforms Found</h5>
          <p class="text-muted-custom text-xs mb-3">
            Register your first platform or web application to track regression test suites, report bugs, and publish releases.
          </p>
          <div>
            <button 
              type="button" 
              class="btn btn-cyan-primary btn-sm fw-bold px-3 py-2" 
              @click="openAddPlatformModal"
              :disabled="isSubmitting"
            >
              <i class="fa-solid fa-plus me-1"></i> Register Platform
            </button>
          </div>
        </div>

        <!-- 3-Column Hub Cards Grid -->
        <div v-else class="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4 mb-5">
          <div v-for="plat in filteredPlatforms" :key="plat.id" class="col">
            <HubCard
              :platform="plat"
              :initialTab="globalCardTab"
              @open-platform="handleOpenPlatform"
              @open-settings="handleOpenSettings"
              @delete-platform="confirmDeletePlatform"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── VIEW 2: SELECTED PLATFORM WORKSPACE (selectedPlatform) ────────── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div v-else class="hub-workspace-view">
      <!-- Unified Workspace Header & Module Switcher -->
      <HubWorkspaceHeader
        :platform="selectedPlatform"
        :platforms="platforms"
        :activeModule="activeModule"
        :stats="selectedPlatform.hub_stats || {}"
        @back-to-hub="handleBackToHub"
        @switch-platform="handleSwitchPlatform"
        @switch-module="handleSwitchModule"
        @open-settings="handleOpenSettings"
      />

      <!-- Embedded Active Module Container -->
      <div class="hub-embedded-module-container">
        <!-- Module 1: Regression Library -->
        <RegressionLibraryDashboard
          v-if="activeModule === 'regression'"
          :embeddedMode="true"
          :embeddedPlatform="selectedPlatform"
        />

        <!-- Module 2: Platform Bug Reporting -->
        <PlatformBugReportingDashboard
          v-else-if="activeModule === 'bug_report'"
          :embeddedMode="true"
          :embeddedPlatform="selectedPlatform"
        />

        <!-- Module 3: Platform Releases -->
        <PlatformReleasesDashboard
          v-else-if="activeModule === 'releases'"
          :embeddedMode="true"
          :embeddedPlatform="selectedPlatform"
        />
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODAL 1: REGISTER NEW PLATFORM ─────────────────────────────────── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div class="modal fade" id="newPlatformModal" tabindex="-1" aria-hidden="true" ref="newPlatformModal">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 540px;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom py-3 px-4">
            <h5 class="modal-title fw-bold text-dark-blue mb-0">
              <i class="fa-solid fa-plus-circle text-info me-2"></i> Register New Platform
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="submitCreatePlatform">
            <div class="modal-body p-4">
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Name *</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm" 
                  v-model="newPlatformForm.name" 
                  placeholder="e.g. Spark Core, XSurflog" 
                  required 
                />
              </div>

              <div class="row g-2 mb-3">
                <div class="col-6">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Key (Slug) *</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm font-monospace text-lowercase" 
                    v-model="newPlatformForm.key" 
                    placeholder="e.g. spark, xsurflog" 
                    required 
                  />
                </div>
                <div class="col-6">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Regression Prefix</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm font-monospace text-uppercase" 
                    v-model="newPlatformForm.regression_prefix" 
                    placeholder="e.g. SPK, XSL" 
                  />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Description</label>
                <textarea 
                  class="form-control form-control-sm" 
                  rows="2" 
                  v-model="newPlatformForm.description" 
                  placeholder="Summary of this platform, services, and apps..."
                ></textarea>
              </div>

              <!-- Color & Icon Row -->
              <div class="row g-2 mb-3">
                <div class="col-6">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Accent Color</label>
                  <div class="d-flex align-items-center gap-2">
                    <input 
                      type="color" 
                      class="form-control form-control-color border-0 p-0 bg-transparent" 
                      v-model="newPlatformForm.primary_color" 
                      style="width: 32px; height: 32px; cursor: pointer;" 
                    />
                    <input 
                      type="text" 
                      class="form-control form-control-sm font-monospace text-xxs" 
                      v-model="newPlatformForm.primary_color" 
                      style="width: 85px;" 
                    />
                  </div>
                </div>
                <div class="col-6">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Icon Class</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm font-monospace text-xs" 
                    v-model="newPlatformForm.icon" 
                    placeholder="fa-solid fa-cubes" 
                  />
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Jira Project Key</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm font-monospace text-uppercase fw-bold" 
                  v-model="newPlatformForm.jira_project_key" 
                  placeholder="DEV" 
                />
              </div>
            </div>
            <div class="modal-footer bg-light px-4 py-3">
              <button type="button" class="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn btn-sm btn-cyan-primary fw-bold px-4" :disabled="isSubmitting">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <span>Register Platform</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODAL 2: UNIFIED PLATFORM SETTINGS (4 CLEAN TABS) ──────────────── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div class="modal fade" id="unifiedSettingsModal" tabindex="-1" aria-hidden="true" ref="unifiedSettingsModal">
      <div class="modal-dialog modal-dialog-scrollable" style="max-width: 660px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom-0 pb-2 sticky-top px-4 pt-3">
            <div class="d-flex align-items-center gap-2">
              <h5 class="modal-title fw-bold text-dark-blue mb-0">
                <i class="fa-solid fa-sliders text-info me-2"></i> Platform Settings
              </h5>
              <span v-if="editPlatformForm.key" class="badge bg-light text-secondary border font-monospace text-xxs">
                {{ editPlatformForm.key }}
              </span>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <!-- Modal 4-Tab Navigation Strip -->
          <div class="org-tabs-container">
            <div class="org-tabs-nav">
              <button
                type="button"
                class="org-tab-btn"
                :class="{ active: settingsActiveTab === 'general' }"
                @click="settingsActiveTab = 'general'"
              >
                <i class="fa-solid fa-circle-info me-2"></i>
                <span>General</span>
              </button>
              <button
                type="button"
                class="org-tab-btn"
                :class="{ active: settingsActiveTab === 'regression' }"
                @click="settingsActiveTab = 'regression'"
              >
                <i class="fa-solid fa-list-check me-2"></i>
                <span>Regression</span>
              </button>
              <button
                type="button"
                class="org-tab-btn"
                :class="{ active: settingsActiveTab === 'bug_report' }"
                @click="settingsActiveTab = 'bug_report'"
              >
                <i class="fa-solid fa-bug me-2"></i>
                <span>Bug Report &amp; Widget</span>
              </button>
              <button
                type="button"
                class="org-tab-btn"
                :class="{ active: settingsActiveTab === 'releases' }"
                @click="settingsActiveTab = 'releases'"
              >
                <i class="fa-solid fa-code-branch me-2"></i>
                <span>Releases &amp; Repos</span>
              </button>
            </div>
          </div>

          <div class="modal-body p-4">
            <!-- ── TAB 1: GENERAL ── -->
            <div v-show="settingsActiveTab === 'general'">
              <div class="row g-3 mb-3">
                <div class="col-md-7">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Name *</label>
                  <input type="text" class="form-control form-control-sm" v-model="editPlatformForm.name" required />
                </div>
                <div class="col-md-5">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Key (Slug) *</label>
                  <input type="text" class="form-control form-control-sm font-monospace" v-model="editPlatformForm.key" required />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Description</label>
                <textarea 
                  class="form-control form-control-sm" 
                  rows="2" 
                  v-model="editPlatformForm.description" 
                  placeholder="Platform overview and services..."
                ></textarea>
              </div>

              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 mb-3 bg-light rounded-3 border">
                <span class="fw-bold text-secondary text-xxs text-uppercase">
                  <i class="fa-solid fa-palette text-info me-2"></i> Accent Color
                </span>
                <div class="d-flex align-items-center gap-2">
                  <input 
                    type="color" 
                    class="form-control form-control-color border-0 p-0 bg-transparent" 
                    v-model="editPlatformForm.primary_color" 
                    style="width: 24px; height: 24px; cursor: pointer;" 
                  />
                  <input 
                    type="text" 
                    class="form-control form-control-sm text-xxs font-monospace bg-white" 
                    v-model="editPlatformForm.primary_color" 
                    style="width: 78px; height: 26px;" 
                  />
                  <div class="d-flex align-items-center gap-1 ms-1">
                    <button
                      v-for="color in presetColors"
                      :key="'color-' + color"
                      type="button"
                      class="color-preset-dot border-0"
                      :class="{ 'active': (editPlatformForm.primary_color || '').toLowerCase() === color.toLowerCase() }"
                      :style="{ backgroundColor: color }"
                      @click="editPlatformForm.primary_color = color"
                    ></button>
                  </div>
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Icon (FontAwesome class or image URL)</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm font-monospace text-xs" 
                  v-model="editPlatformForm.icon" 
                  placeholder="fa-solid fa-shapes or https://..." 
                />
              </div>
            </div>

            <!-- ── TAB 2: REGRESSION LIBRARY ── -->
            <div v-show="settingsActiveTab === 'regression'">
              <div class="p-3 bg-light rounded-3 border mb-3">
                <span class="fw-bold text-dark-blue text-xs mb-2 d-block">
                  <i class="fa-solid fa-list-check text-info me-2"></i> Regression Suite Configuration
                </span>
                <div class="row g-2 mb-2">
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Test Key Prefix *</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm font-monospace text-uppercase fw-bold bg-white" 
                      v-model="editPlatformForm.regression_prefix" 
                      placeholder="SPK" 
                    />
                    <small class="text-muted-custom text-xxs">e.g. SPK-001, XSL-104</small>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Sprint Auto-Naming</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm font-monospace bg-white" 
                      :value="editPlatformForm.regression_prefix ? (editPlatformForm.regression_prefix + '26.01') : 'SPK26.01'" 
                      disabled 
                    />
                    <small class="text-muted-custom text-xxs">Calculated: {PREFIX}{YY}.{NN}</small>
                  </div>
                </div>

                <div class="row g-2">
                  <div class="col-12">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Regression Jira Board URL</label>
                    <input 
                      type="url" 
                      class="form-control form-control-sm text-xs bg-white" 
                      v-model="editPlatformForm.regression_jira_board_link" 
                      placeholder="https://vertex.atlassian.net/jira/your-board" 
                    />
                    <small class="text-muted-custom text-xxs">Direct link to platform's Jira regression board or agile view</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── TAB 3: BUG REPORT & WIDGET ── -->
            <div v-show="settingsActiveTab === 'bug_report'">
              <!-- Floating Widget Toggle & Appearance -->
              <div class="p-3 bg-light rounded-3 border mb-3">
                <div class="form-check form-switch d-flex align-items-center justify-content-between ms-0 mb-3">
                  <label class="form-check-label text-xs text-dark-blue fw-bold" for="hubWidgetEnabledSwitch">
                    Floating Bug Reporting Widget Enabled
                  </label>
                  <input 
                    class="form-check-input mt-0 ms-2" 
                    type="checkbox" 
                    role="switch" 
                    id="hubWidgetEnabledSwitch" 
                    v-model="editPlatformForm.widget_enabled" 
                    style="width: 2.4em; height: 1.25em; cursor: pointer;" 
                  />
                </div>

                <div class="row g-2 mb-2">
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Button Text</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm bg-white" 
                      v-model="editPlatformForm.widget_button_text" 
                      placeholder="Report a Bug" 
                    />
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Widget Position</label>
                    <select class="form-select form-select-sm bg-white text-xs" v-model="editPlatformForm.widget_position">
                      <option value="right-edge">Right Edge (Floating Tab)</option>
                      <option value="left-edge">Left Edge (Floating Tab)</option>
                      <option value="bottom-right">Bottom Right (Floating Pill)</option>
                      <option value="bottom-left">Bottom Left (Floating Pill)</option>
                      <option value="top-right">Top Right (Floating Pill)</option>
                    </select>
                  </div>
                </div>

                <div class="row g-2">
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Widget Theme</label>
                    <select class="form-select form-select-sm bg-white text-xs" v-model="editPlatformForm.widget_theme">
                      <option value="light">Light Theme</option>
                      <option value="dark">Dark Theme</option>
                    </select>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Widget UUID</label>
                    <div class="input-group input-group-sm">
                      <input 
                        type="text" 
                        class="form-control form-control-sm font-monospace text-xxs bg-white" 
                        :value="editPlatformForm.widget_uuid || 'Auto-generated'" 
                        readonly 
                      />
                      <button 
                        type="button" 
                        class="btn btn-outline-secondary btn-sm" 
                        @click="copyText(editPlatformForm.widget_uuid)" 
                        title="Copy UUID"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Jira Board Routing -->
              <div class="p-3 bg-light rounded-3 border">
                <span class="fw-bold text-dark-blue text-xs mb-2 d-block">
                  <i class="fa-solid fa-diagram-project text-info me-2"></i> Jira Board Routing
                </span>
                <div class="row g-2 mb-2">
                  <div class="col-sm-4">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Board Key *</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm font-monospace text-uppercase fw-bold bg-white" 
                      v-model="editPlatformForm.jira_project_key" 
                      placeholder="DEV" 
                    />
                  </div>
                  <div class="col-sm-8">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Jira Cloud Domain</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm font-monospace bg-white" 
                      v-model="editPlatformForm.jira_domain" 
                      placeholder="vertex.atlassian.net" 
                    />
                  </div>
                </div>
                <div class="form-check form-switch pt-2 border-top d-flex align-items-center justify-content-between ms-0 mb-0">
                  <label class="form-check-label text-xs text-dark-blue fw-semibold" for="hubJiraAutoSync">
                    Auto-push bug submissions directly to <strong>{{ (editPlatformForm.jira_project_key || 'DEV').toUpperCase() }}</strong> board
                  </label>
                  <input 
                    class="form-check-input mt-0 ms-2" 
                    type="checkbox" 
                    role="switch" 
                    id="hubJiraAutoSync" 
                    v-model="editPlatformForm.jira_auto_sync" 
                    style="width: 2.4em; height: 1.25em; cursor: pointer;" 
                  />
                </div>
              </div>
            </div>

            <!-- ── TAB 4: RELEASES & REPOS ── -->
            <div v-show="settingsActiveTab === 'releases'">
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">
                  <i class="fa-brands fa-bitbucket text-info me-2"></i>Bitbucket Workspace
                </label>
                <input 
                  type="text" 
                  class="form-control form-control-sm font-monospace" 
                  v-model="editPlatformForm.bitbucket_workspace" 
                  @change="fetchBitbucketRepositories(editPlatformForm.bitbucket_workspace)"
                  placeholder="VertexSecurity" 
                />
              </div>

              <!-- Repositories List -->
              <div class="repo-section-card">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <span class="fw-bold text-dark-blue text-xs">
                    <i class="fa-solid fa-code-branch text-info me-2"></i> Bitbucket Repositories
                  </span>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-white text-secondary border font-monospace text-xxs d-inline-flex align-items-center" style="height: 24px; padding: 0 10px; border-radius: 6px;">
                      {{ (settingsForm.repositories || []).length }} Attached &bull; {{ bitbucketRepoOptions.length }} Available
                    </span>
                    <button
                      type="button"
                      class="btn btn-white bg-white border text-info p-0 d-inline-flex align-items-center justify-content-center"
                      style="width: 24px; height: 24px; border-radius: 6px;"
                      @click="fetchBitbucketRepositories(settingsForm.bitbucket_workspace)"
                      title="Refresh repositories from Bitbucket Cloud"
                    >
                      <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isLoadingBitbucketRepos }" style="font-size: 11px;"></i>
                    </button>
                  </div>
                </div>

                <!-- Streamlined Bitbucket Repository <select> Dropdown -->
                <div class="d-flex align-items-center gap-2 mb-2">
                  <select
                    class="form-select text-xs font-monospace bg-white flex-grow-1 shadow-none"
                    style="height: 36px; border-radius: 8px; border-color: #cbd5e1; font-size: 12.5px;"
                    v-model="repoPicker.settings.selectedSlug"
                    @change="onRepoDropdownSelect('settings')"
                  >
                    <option value="">+ Attach a Bitbucket repository ({{ bitbucketRepoOptions.length }} available)...</option>
                    <option
                      v-for="opt in bitbucketRepoOptions"
                      :key="'s-repo-' + opt.repo_slug"
                      :value="opt.repo_slug"
                    >
                      {{ opt.repo_slug }}  —  {{ (opt.suggested_role || opt.role || 'repo').toUpperCase() }}{{ isRepoAttached(opt.repo_slug) ? ' (Attached)' : '' }}
                    </option>
                    <option value="__custom__">+ Enter custom repository slug...</option>
                  </select>
                </div>

                <!-- Custom Repository Input Row -->
                <div v-if="repoPicker.settings.selectedSlug === '__custom__'" class="d-flex align-items-center gap-2 mb-2">
                  <input
                    type="text"
                    class="form-control form-control-sm text-xs font-monospace bg-white flex-grow-1"
                    v-model="repoPicker.settings.customSlug"
                    placeholder="Enter Bitbucket repo slug (e.g. xsurflog_be)"
                    @keydown.enter.prevent="addRepositoryFromPicker('settings')"
                  />
                  <select
                    class="form-select form-select-sm text-xs font-monospace bg-white"
                    v-model="repoPicker.settings.role"
                    style="width: 110px;"
                  >
                    <option v-for="rOpt in repoRoleOptions" :key="'custom-r-' + rOpt.value" :value="rOpt.value">
                      {{ rOpt.shortLabel || rOpt.value.toUpperCase() }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-cyan-primary btn-xs px-3 py-1 fw-bold flex-shrink-0"
                    @click="addRepositoryFromPicker('settings')"
                    :disabled="!repoPicker.settings.customSlug"
                  >
                    <i class="fa-solid fa-plus me-1"></i>Attach
                  </button>
                </div>

                <!-- Attached List -->
                <div v-if="settingsForm.repositories && settingsForm.repositories.length > 0">
                  <div class="text-xxs fw-bold text-uppercase text-secondary mb-2 mt-3" style="letter-spacing: 0.05em;">
                    Attached Repositories
                  </div>
                  <div class="d-flex flex-column gap-2">
                    <div
                      v-for="(repo, rIdx) in settingsForm.repositories"
                      :key="'s-attached-' + rIdx + '-' + repo.repo_slug"
                      class="d-flex align-items-center justify-content-between repo-attached-row"
                    >
                      <div class="d-flex align-items-center gap-3 min-w-0 flex-grow-1" style="gap: 14px !important;">
                        <select
                          class="repo-role-badge-select"
                          :class="'role-' + (repo.role || 'repo')"
                          v-model="repo.role"
                          title="Change repository role"
                        >
                          <option v-for="rOpt in repoRoleOptions" :key="'s-r-' + rOpt.value" :value="rOpt.value">
                            {{ rOpt.shortLabel || rOpt.value.toUpperCase() }}
                          </option>
                        </select>
                        <div class="d-flex align-items-baseline text-truncate font-monospace" style="font-size: 12.5px;">
                          <span class="text-muted-custom fw-normal">{{ repo.workspace || settingsForm.bitbucket_workspace || 'VertexSecurity' }}</span>
                          <span class="text-muted-custom" style="margin: 0 3px;">/</span>
                          <span class="fw-bold text-dark-blue text-truncate">{{ repo.repo_slug }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                        <span class="repo-branch-pill">
                          <i class="fa-solid fa-code-branch me-1 text-info"></i>{{ repo.branch || 'dev' }}
                        </span>
                        <button
                          type="button"
                          class="repo-remove-btn"
                          @click="removeRepository(rIdx)"
                          title="Remove repository"
                        >
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-xxs text-muted-custom px-3 py-3 bg-white rounded-2 border text-center mt-3">
                  No repositories attached yet. Select a repository above to attach it.
                </div>
              </div>
            </div>
          </div>

          <!-- Settings Modal Footer -->
          <div class="modal-footer bg-light px-4 py-3 d-flex justify-content-between">
            <button type="button" class="btn btn-sm btn-light border fw-semibold text-secondary px-3 py-2 rounded-2" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-sm btn-cyan-primary fw-bold px-4 py-2 rounded-2" @click="savePlatformSettings" :disabled="isSavingSettings">
              <span v-if="isSavingSettings" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <i v-else class="fa-solid fa-check me-2"></i>
              <span>Save Platform Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODAL 3: CONFIRM DELETE PLATFORM ───────────────────────────────── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div class="modal fade" id="deletePlatformModal" tabindex="-1" aria-hidden="true" ref="deletePlatformModal">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 440px;">
        <div class="modal-content border-0 shadow">
          <div class="modal-body p-4 text-center">
            <div class="mb-3 text-danger">
              <i class="fa-solid fa-triangle-exclamation fa-3x"></i>
            </div>
            <h5 class="fw-bold text-dark-blue mb-2">Delete Platform?</h5>
            <p class="text-muted-custom text-xs mb-4">
              Are you sure you want to delete <strong>{{ platformToDelete ? platformToDelete.name : 'this platform' }}</strong>?
              This will unbind its regression tests, bug reports, and releases.
            </p>
            <div class="d-flex align-items-center justify-content-center gap-2">
              <button type="button" class="btn btn-sm btn-outline-secondary px-3 py-2" data-bs-dismiss="modal">
                Cancel
              </button>
              <button 
                type="button" 
                class="btn btn-sm btn-danger fw-bold px-3 py-2" 
                @click="deletePlatformConfirmed"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <span>Yes, Delete Platform</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal as BootstrapModal } from "bootstrap";
import HubCard from "./components/HubCard.vue";
import HubWorkspaceHeader from "./components/HubWorkspaceHeader.vue";
import RegressionLibraryDashboard from "@/views/Pages/RegressionLibrary/Dashboard.vue";
import PlatformBugReportingDashboard from "@/views/Pages/PlatformBugReporting/Dashboard.vue";
import PlatformReleasesDashboard from "@/views/Pages/PlatformReleases/Dashboard.vue";

export default {
  name: "UnifiedPlatformHubDashboard",
  components: {
    HubCard,
    HubWorkspaceHeader,
    RegressionLibraryDashboard,
    PlatformBugReportingDashboard,
    PlatformReleasesDashboard
  },
  props: {
    initialModule: {
      type: String,
      default: "regression"
    }
  },
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      isSavingSettings: false,
      platforms: [],
      searchQuery: "",
      globalCardTab: "regression",
      selectedPlatform: null,
      activeModule: this.initialModule || "regression",

      // Settings Modal State
      settingsActiveTab: "general",
      editPlatformForm: {
        id: null,
        name: "",
        key: "",
        description: "",
        primary_color: "#00a0c1",
        icon: "fa-solid fa-shapes",
        regression_prefix: "",
        regression_jira_board_link: "",
        widget_enabled: true,
        widget_button_text: "Report a Bug",
        widget_position: "right-edge",
        widget_theme: "light",
        widget_uuid: "",
        jira_domain: "vertex.atlassian.net",
        jira_project_key: "DEV",
        jira_auto_sync: true,
        bitbucket_workspace: "VertexSecurity",
        repositories: []
      },
      newRepoSlugInput: "",
      newRepoRoleInput: "frontend",
      presetColors: ["#00a0c1", "#0ea5e9", "#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b"],

      // Bitbucket Repositories Dropdown (populated via General Token in .env)
      isLoadingBitbucketRepos: false,
      bitbucketRepoOptions: [
        { role: "be", suggested_role: "be", workspace: "VertexSecurity", repo_slug: "spark_go", full_name: "VertexSecurity/spark_go", branch: "dev" },
        { role: "fe", suggested_role: "fe", workspace: "VertexSecurity", repo_slug: "spark_vuejs", full_name: "VertexSecurity/spark_vuejs", branch: "dev" },
        { role: "be", suggested_role: "be", workspace: "VertexSecurity", repo_slug: "xsurflog_be", full_name: "VertexSecurity/xsurflog_be", branch: "dev" },
        { role: "fe", suggested_role: "fe", workspace: "VertexSecurity", repo_slug: "xsurflog_fe", full_name: "VertexSecurity/xsurflog_fe", branch: "dev" },
        { role: "extension", suggested_role: "extension", workspace: "VertexSecurity", repo_slug: "xsurflog_extension", full_name: "VertexSecurity/xsurflog_extension", branch: "dev" },
        { role: "user-agent", suggested_role: "user-agent", workspace: "VertexSecurity", repo_slug: "xsurflog_user_agent", full_name: "VertexSecurity/xsurflog_user_agent", branch: "dev" }
      ],
      repoRoleOptions: [
        { value: "be", label: "BE (Backend)", shortLabel: "BE" },
        { value: "fe", label: "FE (Frontend)", shortLabel: "FE" },
        { value: "extension", label: "Extension", shortLabel: "EXT" },
        { value: "user-agent", label: "User Agent", shortLabel: "AGENT" },
        { value: "worker", label: "Worker / Service", shortLabel: "WORKER" },
        { value: "repo", label: "General Repo", shortLabel: "REPO" }
      ],
      repoPicker: {
        settings: { selectedSlug: "", customSlug: "", role: "be" }
      },

      // Create Platform Modal State
      newPlatformForm: {
        name: "",
        key: "",
        regression_prefix: "",
        description: "",
        primary_color: "#00a0c1",
        icon: "fa-solid fa-shapes",
        jira_project_key: "DEV"
      },

      // Delete Modal State
      platformToDelete: null
    };
  },
  computed: {
    settingsForm() {
      return this.editPlatformForm;
    },
    filteredPlatforms() {
      if (!this.searchQuery) return this.platforms;
      const q = this.searchQuery.toLowerCase().trim();
      return this.platforms.filter((p) => {
        return (
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.key && p.key.toLowerCase().includes(q)) ||
          (p.description && p.description.toLowerCase().includes(q)) ||
          (p.regression_prefix && p.regression_prefix.toLowerCase().includes(q))
        );
      });
    },
    totalRegressionTests() {
      return this.platforms.reduce((sum, p) => {
        return sum + (p.hub_stats?.regression?.tests ?? p.total_tests ?? 0);
      }, 0);
    },
    totalOpenBugs() {
      return this.platforms.reduce((sum, p) => {
        return sum + (p.hub_stats?.bug_report?.open ?? p.open_bugs_count ?? p.open_bugs ?? 0);
      }, 0);
    },
    totalReleasesCount() {
      return this.platforms.reduce((sum, p) => {
        return sum + (p.hub_stats?.releases?.total ?? p.total_releases ?? 0);
      }, 0);
    }
  },
  created() {
    this.fetchPlatforms();
  },
  mounted() {
    this.fetchBitbucketRepositories();
  },
  methods: {
    async fetchPlatforms() {
      this.isLoading = true;
      try {
        // Step 1: Attempt to fetch from unified hub endpoint
        let loaded = false;
        try {
          const res = await this.$axios.get("/api/platforms/hub");
          if (res.data && (res.data.platforms || res.data.data)) {
            const list = res.data.platforms || res.data.data || [];
            if (Array.isArray(list) && list.length > 0) {
              this.platforms = list;
              loaded = true;
            }
          }
        } catch (e) {
          // Hub endpoint not available, smoothly fall back to aggregating module endpoints
        }

        // Step 2: Fallback aggregation from bug-reporting, regression, and platform-guide endpoints
        if (!loaded) {
          const [bugRes, regRes, guideRes] = await Promise.allSettled([
            this.$axios.get("/api/platform-bug-reporting/platforms"),
            this.$axios.get("/regression/projects"),
            this.$axios.get("/api/platform-guide/platforms")
          ]);

          let bugPlatforms = [];
          if (bugRes.status === "fulfilled" && bugRes.value?.data) {
            bugPlatforms = bugRes.value.data.platforms || bugRes.value.data.data || [];
          }

          let regProjects = [];
          if (regRes.status === "fulfilled" && regRes.value?.data) {
            regProjects = regRes.value.data.projects || [];
          }

          let guidePlatforms = [];
          if (guideRes.status === "fulfilled" && guideRes.value?.data) {
            guidePlatforms = guideRes.value.data.platforms || guideRes.value.data.data || [];
          }

          // Build unified merged platforms list
          const platformMap = new Map();

          // 1. Bug platforms provide primary structure
          bugPlatforms.forEach((bp) => {
            const key = (bp.key || "").toLowerCase();
            platformMap.set(key, {
              ...bp,
              hub_stats: {
                regression: { suites: 0, tests: 0, pass_rate: 100, passed: 0, failed: 0, active_sprint: "Active Library" },
                bug_report: {
                  open: bp.open_bugs_count ?? bp.open_bugs ?? 0,
                  resolved: bp.resolved_bugs_count ?? bp.resolved_bugs ?? 0,
                  repos: bp.repositories ? bp.repositories.length : 0
                },
                releases: {
                  total: bp.total_releases ?? 0,
                  latest_version: bp.latest_version || "v1.0.0",
                  prod_deploys: bp.prod_deploys ?? (bp.total_releases ?? 0),
                  repos: bp.repositories ? bp.repositories.length : 0
                }
              }
            });
          });

          // 2. Merge Regression projects
          regProjects.forEach((rp) => {
            // Match by name or prefix
            let foundKey = null;
            for (const [k, p] of platformMap.entries()) {
              if (
                p.name.toLowerCase() === rp.name.toLowerCase() ||
                (p.key && rp.name.toLowerCase().includes(p.key.toLowerCase())) ||
                (p.regression_prefix && p.regression_prefix.toLowerCase() === (rp.prefix || "").toLowerCase())
              ) {
                foundKey = k;
                break;
              }
            }

            const regStats = {
              suites: rp.total_sprints || 0,
              tests: rp.total_tests || 0,
              pass_rate: rp.total_tests > 0 ? Math.round((rp.passed_tests / rp.total_tests) * 100) : 100,
              passed: rp.passed_tests || 0,
              failed: Math.max(0, (rp.total_tests || 0) - (rp.passed_tests || 0)),
              active_sprint: rp.active_sprint_name || "Active Library"
            };

            if (foundKey && platformMap.has(foundKey)) {
              const existing = platformMap.get(foundKey);
              existing.regression_prefix = existing.regression_prefix || rp.prefix || "";
              existing.total_tests = rp.total_tests || 0;
              existing.passed_tests = rp.passed_tests || 0;
              existing.total_sprints = rp.total_sprints || 0;
              existing.active_sprint_name = rp.active_sprint_name || "";
              existing.regression_project_id = rp.id;
              existing.hub_stats.regression = regStats;
            } else {
              // Create new entry
              const k = (rp.prefix || rp.name || `proj-${rp.id}`).toLowerCase().replace(/[^a-z0-9]/g, "-");
              platformMap.set(k, {
                id: rp.id,
                name: rp.name,
                key: k,
                description: rp.description,
                icon: rp.icon || "fa-solid fa-list-check",
                primary_color: rp.color || "#00a0c1",
                regression_prefix: rp.prefix || "",
                regression_project_id: rp.id,
                total_tests: rp.total_tests || 0,
                passed_tests: rp.passed_tests || 0,
                total_sprints: rp.total_sprints || 0,
                active_sprint_name: rp.active_sprint_name || "",
                hub_stats: {
                  regression: regStats,
                  bug_report: { open: 0, resolved: 0, repos: 0 },
                  releases: { total: 0, latest_version: "v1.0.0", prod_deploys: 0, repos: 0 }
                }
              });
            }
          });

          // 3. Merge Guide platforms for changelog/releases info
          guidePlatforms.forEach((gp) => {
            const k = (gp.key || "").toLowerCase();
            if (platformMap.has(k)) {
              const existing = platformMap.get(k);
              existing.total_releases = gp.total_releases || existing.total_releases || 0;
              existing.latest_version = gp.latest_version || existing.latest_version || "v1.0.0";
              existing.bitbucket_workspace = gp.bitbucket_workspace || existing.bitbucket_workspace || "VertexSecurity";
              existing.hub_stats.releases.total = existing.total_releases;
              existing.hub_stats.releases.latest_version = existing.latest_version;
            }
          });

          this.platforms = Array.from(platformMap.values());
        }

        // Check URL query parameters to auto-select platform
        const queryPlatform = this.$route.query.platform;
        const queryModule = this.$route.query.module || this.$route.query.tab;
        if (queryPlatform && this.platforms.length > 0) {
          const matched = this.platforms.find(
            (p) => (p.key && p.key.toLowerCase() === queryPlatform.toLowerCase()) || String(p.id) === String(queryPlatform)
          );
          if (matched) {
            this.selectedPlatform = matched;
            if (queryModule && ["regression", "bug_report", "releases"].includes(queryModule)) {
              this.activeModule = queryModule;
            }
          }
        }
      } catch (err) {
        console.error("Error fetching platform hub data:", err);
        if (this.$toast) {
          this.$toast.error("Failed to load platforms");
        }
      } finally {
        this.isLoading = false;
      }
    },

    handleOpenPlatform({ platform, module }) {
      this.selectedPlatform = platform;
      this.activeModule = module || "regression";
      this.syncUrlQuery();
    },

    handleSwitchPlatform(plat) {
      this.selectedPlatform = plat;
      this.syncUrlQuery();
    },

    handleSwitchModule(mod) {
      this.activeModule = mod;
      this.syncUrlQuery();
    },

    handleBackToHub() {
      this.selectedPlatform = null;
      this.syncUrlQuery();
    },

    syncUrlQuery() {
      if (this.selectedPlatform) {
        this.$router.replace({
          query: {
            platform: this.selectedPlatform.key,
            module: this.activeModule
          }
        }).catch(() => {});
      } else {
        this.$router.replace({ query: {} }).catch(() => {});
      }
    },

    // ── SETTINGS MODAL ──
    handleOpenSettings({ platform, initialTab }) {
      this.editPlatformForm = {
        id: platform.id,
        name: platform.name || "",
        key: platform.key || "",
        description: platform.description || "",
        primary_color: platform.primary_color || "#00a0c1",
        icon: platform.icon || "fa-solid fa-shapes",
        regression_prefix: platform.regression_prefix || platform.key?.toUpperCase() || "",
        regression_jira_board_link: platform.regression_jira_board_link || "",
        widget_enabled: platform.widget_enabled !== false,
        widget_button_text: platform.widget_button_text || "Report a Bug",
        widget_position: platform.widget_position || "right-edge",
        widget_theme: platform.widget_theme || "light",
        widget_uuid: platform.widget_uuid || "",
        jira_domain: platform.jira_domain || "vertex.atlassian.net",
        jira_project_key: platform.jira_project_key || "DEV",
        jira_auto_sync: platform.jira_auto_sync !== false,
        bitbucket_workspace: platform.bitbucket_workspace || "VertexSecurity",
        repositories: platform.repositories ? JSON.parse(JSON.stringify(platform.repositories)) : []
      };

      this.repoPicker.settings = { selectedSlug: "", customSlug: "", role: "be" };
      this.fetchBitbucketRepositories(this.editPlatformForm.bitbucket_workspace);

      if (initialTab && ["general", "regression", "bug_report", "releases"].includes(initialTab)) {
        this.settingsActiveTab = initialTab;
      } else {
        this.settingsActiveTab = "general";
      }

      this.openModalSafe("unifiedSettingsModal");
    },

    openSettingsModal(platform, initialTab) {
      this.handleOpenSettings({ platform, initialTab });
    },

    fetchBitbucketRepositories(workspace) {
      const ws = (workspace || this.editPlatformForm?.bitbucket_workspace || "VertexSecurity").trim() || "VertexSecurity";
      this.isLoadingBitbucketRepos = true;

      const handleSuccess = (res) => {
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          this.bitbucketRepoOptions = res.data.data.map((r) => {
            const detectedRole = r.suggested_role || r.role || "repo";
            return {
              role: detectedRole,
              suggested_role: detectedRole,
              workspace: r.workspace || ws,
              repo_slug: r.repo_slug,
              full_name: r.full_name || `${r.workspace || ws}/${r.repo_slug}`,
              repo_url: r.repo_url || `https://bitbucket.org/${r.workspace || ws}/${r.repo_slug}`,
              branch: r.branch || "dev"
            };
          });
        }
      };

      this.$axios
        .get("/api/platform-bug-reporting/bitbucket/repositories", { params: { workspace: ws } })
        .then(handleSuccess)
        .catch(() => {
          this.$axios
            .get("/api/bug-reporting/bitbucket/repositories", { params: { workspace: ws } })
            .then(handleSuccess)
            .catch(() => {
              // Keep seeded fallback catalog if API unreachable
            });
        })
        .finally(() => {
          this.isLoadingBitbucketRepos = false;
        });
    },

    isRepoAttached(arg1, arg2) {
      const slug = arg2 !== undefined ? arg2 : arg1;
      if (!slug || !this.editPlatformForm || !Array.isArray(this.editPlatformForm.repositories)) return false;
      const s = slug.toLowerCase().trim();
      return this.editPlatformForm.repositories.some((r) => (r.repo_slug || "").toLowerCase().trim() === s);
    },

    onRepoDropdownSelect(target = "settings") {
      const picker = this.repoPicker[target];
      if (!picker || !picker.selectedSlug || picker.selectedSlug === "__custom__") return;
      const found = this.bitbucketRepoOptions.find((opt) => opt.repo_slug === picker.selectedSlug);
      if (found) {
        picker.role = found.suggested_role || found.role || "repo";
      }
      this.addRepositoryFromPicker(target);
    },

    addRepositoryFromPicker(target = "settings") {
      const picker = this.repoPicker[target];
      if (!picker) return;

      const rawSlug = picker.selectedSlug === "__custom__" ? picker.customSlug : picker.selectedSlug;
      let slug = (rawSlug || "").trim();
      if (!slug) return;

      let ws = (this.editPlatformForm.bitbucket_workspace || "VertexSecurity").trim() || "VertexSecurity";
      if (slug.includes("/")) {
        const parts = slug.split("/").filter(Boolean);
        if (parts.length >= 2) {
          ws = parts[parts.length - 2];
          slug = parts[parts.length - 1];
        }
      }
      slug = slug.replace(/\.git$/i, "");

      if (!Array.isArray(this.editPlatformForm.repositories)) {
        this.editPlatformForm.repositories = [];
      }

      const foundOpt = this.bitbucketRepoOptions.find(
        (o) => (o.repo_slug || "").toLowerCase() === slug.toLowerCase()
      );
      const role = (picker.role || (foundOpt && (foundOpt.suggested_role || foundOpt.role)) || "repo").toLowerCase();
      const exists = this.editPlatformForm.repositories.some(
        (r) => (r.repo_slug || "").toLowerCase() === slug.toLowerCase()
      );
      if (exists) {
        if (this.$toast) {
          this.$toast.info(`Repository ${ws}/${slug} is already attached.`);
        }
        picker.selectedSlug = "";
        picker.customSlug = "";
        return;
      }

      this.editPlatformForm.repositories.push({
        role,
        workspace: ws,
        repo_slug: slug,
        repo_url: `https://bitbucket.org/${ws}/${slug}`,
        branch: (foundOpt && foundOpt.branch) || "dev"
      });

      if (!foundOpt) {
        this.bitbucketRepoOptions.push({
          role,
          suggested_role: role,
          workspace: ws,
          repo_slug: slug,
          full_name: `${ws}/${slug}`,
          repo_url: `https://bitbucket.org/${ws}/${slug}`,
          branch: "dev"
        });
      }

      picker.selectedSlug = "";
      picker.customSlug = "";
    },

    addRepository() {
      if (!this.newRepoSlugInput) return;
      if (!this.editPlatformForm.repositories) {
        this.editPlatformForm.repositories = [];
      }
      this.editPlatformForm.repositories.push({
        repo_slug: this.newRepoSlugInput.trim(),
        role: this.newRepoRoleInput || "frontend"
      });
      this.newRepoSlugInput = "";
    },

    removeRepository(idx) {
      if (this.editPlatformForm.repositories) {
        this.editPlatformForm.repositories.splice(idx, 1);
      }
    },

    async savePlatformSettings() {
      this.isSavingSettings = true;
      try {
        const id = this.editPlatformForm.id;
        const payload = { ...this.editPlatformForm };

        let saved = false;
        // Try unified PUT /api/platforms/{id}/settings
        try {
          const res = await this.$axios.put(`/api/platforms/${id}/settings`, payload);
          if (res.data && (res.data.success || res.data.status)) {
            saved = true;
          }
        } catch (e) {
          // Fallback to updating via existing endpoints
        }

        if (!saved) {
          // Fallback update
          await Promise.allSettled([
            this.$axios.post(`/api/platform-bug-reporting/platforms/${id}/settings`, payload),
            this.$axios.post(`/api/platform-guide/platforms/update/${id}`, payload)
          ]);
        }

        // Update local platform in memory
        const idx = this.platforms.findIndex((p) => p.id === id);
        if (idx !== -1) {
          Object.assign(this.platforms[idx], payload);
        }
        if (this.selectedPlatform && this.selectedPlatform.id === id) {
          Object.assign(this.selectedPlatform, payload);
        }

        if (this.$toast) {
          this.$toast.success("Platform settings saved successfully!");
        }
        this.closeModalSafe("unifiedSettingsModal");
      } catch (err) {
        console.error("Error saving platform settings:", err);
        if (this.$toast) {
          this.$toast.error("Failed to save platform settings.");
        }
      } finally {
        this.isSavingSettings = false;
      }
    },

    // ── CREATE PLATFORM MODAL ──
    openAddPlatformModal() {
      this.newPlatformForm = {
        name: "",
        key: "",
        regression_prefix: "",
        description: "",
        primary_color: "#00a0c1",
        icon: "fa-solid fa-shapes",
        jira_project_key: "DEV"
      };
      this.openModalSafe("newPlatformModal");
    },

    async submitCreatePlatform() {
      this.isSubmitting = true;
      try {
        const payload = {
          name: this.newPlatformForm.name.trim(),
          key: this.newPlatformForm.key.trim().toLowerCase(),
          regression_prefix: this.newPlatformForm.regression_prefix.trim().toUpperCase(),
          description: this.newPlatformForm.description.trim(),
          primary_color: this.newPlatformForm.primary_color || "#00a0c1",
          icon: this.newPlatformForm.icon || "fa-solid fa-shapes",
          jira_project_key: this.newPlatformForm.jira_project_key || "DEV"
        };

        // Try creating via guide or bug-reporting platform endpoint
        try {
          await this.$axios.post("/api/platform-guide/platforms", payload);
        } catch (e) {
          // Try regression project create if guide is not available
          await this.$axios.post("/regression/projects", {
            name: payload.name,
            description: payload.description,
            prefix: payload.regression_prefix,
            color: payload.primary_color,
            icon: payload.icon
          });
        }

        if (this.$toast) {
          this.$toast.success("Platform registered successfully!");
        }
        this.closeModalSafe("newPlatformModal");
        await this.fetchPlatforms();
      } catch (err) {
        console.error("Error creating platform:", err);
        if (this.$toast) {
          this.$toast.error("Failed to register platform.");
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    // ── DELETE PLATFORM MODAL ──
    confirmDeletePlatform(platform) {
      this.platformToDelete = platform;
      this.openModalSafe("deletePlatformModal");
    },

    async deletePlatformConfirmed() {
      if (!this.platformToDelete) return;
      this.isSubmitting = true;
      try {
        const id = this.platformToDelete.id;
        try {
          await this.$axios.delete(`/api/platform-guide/platforms/${id}`);
        } catch (e) {
          // fallback delete
          await this.$axios.delete(`/regression/projects/${id}`);
        }

        if (this.$toast) {
          this.$toast.success("Platform removed successfully!");
        }
        this.closeModalSafe("deletePlatformModal");
        if (this.selectedPlatform && this.selectedPlatform.id === id) {
          this.selectedPlatform = null;
        }
        await this.fetchPlatforms();
      } catch (err) {
        console.error("Error deleting platform:", err);
        if (this.$toast) {
          this.$toast.error("Failed to delete platform.");
        }
      } finally {
        this.isSubmitting = false;
        this.platformToDelete = null;
      }
    },

    // ── MODAL UTILITIES ──
    openModalSafe(id) {
      const el = document.getElementById(id);
      if (el) {
        try {
          const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
          if (bootstrapObj && bootstrapObj.Modal) {
            const modal = bootstrapObj.Modal.getInstance(el) || new bootstrapObj.Modal(el);
            modal.show();
          }
        } catch (e) {
          console.error("Error opening modal:", e);
        }
      }
    },

    closeModalSafe(id) {
      const el = document.getElementById(id);
      if (el) {
        const closeBtn = el.querySelector('[data-bs-dismiss="modal"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          try {
            const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
            if (bootstrapObj && bootstrapObj.Modal) {
              const modal = bootstrapObj.Modal.getInstance(el) || new bootstrapObj.Modal(el);
              modal.hide();
            }
          } catch (e) {
            console.error("Error closing modal:", e);
          }
        }
      }
      setTimeout(() => {
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 150);
    },

    async copyText(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        if (this.$toast) {
          this.$toast.success("Copied to clipboard!");
        }
      } catch (e) {
        console.error(e);
      }
    }
  }
};
</script>

<style scoped>
/* ── PAGE HEADER & LAYOUT ── */
.unified-platform-hub-page {
  min-height: 80vh;
}

.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ms-page-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 130, 152, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: #008298;
}

.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.3);
}

.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

/* ── Header Secondary Button ── */
.btn-header-secondary {
  background-color: #ffffff !important;
  color: #334155 !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.btn-header-secondary:hover,
.btn-header-secondary:focus,
.btn-header-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06) !important;
}

.btn-header-secondary:hover i,
.btn-header-secondary:focus i,
.btn-header-secondary:active i {
  color: #008ca9 !important;
}

.btn-header-secondary:hover span,
.btn-header-secondary:focus span,
.btn-header-secondary:active span {
  color: #0f172a !important;
}

.filter-toolbar-card {
  border-radius: 12px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
}

.focus-glow:focus {
  background-color: #ffffff !important;
  box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.25);
}

.color-preset-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.color-preset-dot:hover {
  transform: scale(1.2);
}

.color-preset-dot.active {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 3px #00a0c1;
}

/* ── VERTEX BRAND UTILITIES ── */
.btn-cyan-primary {
  background-color: #00A0C1 !important;
  color: #ffffff !important;
  border: 1px solid #00A0C1 !important;
  border-radius: 8px !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.btn-cyan-primary:hover,
.btn-cyan-primary:focus,
.btn-cyan-primary:active {
  background-color: #0891b2 !important;
  border-color: #0891b2 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.25) !important;
}

.text-dark-blue {
  color: #0f172a !important;
}

.text-muted-custom {
  color: #64748b !important;
}

.text-cyan-primary {
  color: #00A0C1 !important;
}

.bg-cyan-primary {
  background-color: #00A0C1 !important;
}

.bg-cyan-subtle {
  background-color: #ecfeff !important;
}

.text-cyan {
  color: #0891b2 !important;
}

.border-cyan-subtle {
  border-color: #a5f3fc !important;
}

.text-xxs {
  font-size: 0.6875rem !important;
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* ── MODAL TAB NAVIGATION (ORG-TABS) ── */
.org-tabs-container {
  background-color: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.org-tabs-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.org-tab-btn {
  background: none;
  border: none;
  padding: 12px 16px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  outline: none;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  user-select: none;
}

.org-tab-btn:hover {
  color: #00768a;
}

.org-tab-btn.active {
  color: #00768a;
  font-weight: 700;
}

.org-tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00768a;
  border-radius: 3px 3px 0 0;
}

/* ── BITBUCKET REPOSITORY PICKER & ROWS ── */
.repo-section-card {
  padding: 18px 20px !important;
  border-radius: 12px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.repo-attached-row {
  padding: 10px 14px;
  gap: 12px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.repo-attached-row:hover {
  border-color: #bae6fd !important;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.08);
}

.repo-role-badge-select {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 76px !important;
  flex: 0 0 72px !important;
  height: 24px;
  margin-right: 2px;
  padding: 0 18px 0 8px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  text-transform: uppercase;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  background-repeat: no-repeat;
  background-position: right 5px center;
  background-size: 8px 8px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%2364748b' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
  transition: background-color 0.15s ease, border-color 0.15s ease;
  outline: none;
  border: 1px solid transparent;
}

.repo-role-badge-select.role-be {
  background-color: #ecfeff;
  color: #0891b2;
  border: 1px solid #a5f3fc;
}

.repo-role-badge-select.role-fe {
  background-color: #eef2ff;
  color: #4f46e5;
  border: 1px solid #c7d2fe;
}

.repo-role-badge-select.role-extension {
  background-color: #faf5ff;
  color: #9333ea;
  border: 1px solid #e9d5ff;
}

.repo-role-badge-select.role-user-agent {
  background-color: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}

.repo-role-badge-select.role-worker {
  background-color: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.repo-role-badge-select.role-repo {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.repo-role-badge-select:hover,
.repo-role-badge-select:focus {
  filter: brightness(0.97);
  box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.15);
}

.repo-branch-pill {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  background-color: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.repo-remove-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
}

.repo-remove-btn:hover {
  background-color: #fef2f2;
  color: #ef4444;
}

.hover-danger {
  transition: color 0.15s ease, background-color 0.15s ease;
}

.hover-danger:hover {
  color: #dc2626 !important;
  background-color: #fef2f2 !important;
}
</style>
